import type { ReactElement } from "react";
import type { KundeAkte, OverviewMainContact, Unternehmen } from "@kundeninfo/api-client";
import { Karte, Objektsymbol } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { KundenChip } from "../../components/KundenChip.js";
import { mailtoHref, relativeZeit, telHref } from "../../format.js";
import { hausfarbe } from "../../markenfarben.js";
import type { AktenTabId } from "../KundenAkte.js";
import { PartnerRoleBadges } from "../partners/PartnerRoleBadges.js";
import { COV_TEXT } from "./texts.js";

/*
 * Die feste Kopfkachel der Übersicht (371, 383): wer der Kunde ist. Sie liest die schon geladene Akte; vom Übersichtsabruf kommt nur der
 * Hauptansprechpartner, und über seinen Zustand rät sie nichts: Wer ihn nicht geladen hat, sagt „nicht geladen“ und nie „Kein Hauptansprechpartner“.
 */

/** Der Hauptansprechpartner vom Übersichtsabruf: noch unterwegs, Abruf oder Quelle ausgefallen, oder geladen (dann vielleicht keiner). */
export type ContactState =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "ready"; contact: OverviewMainContact | null };

export type HeaderCardProps = {
  customer: KundeAkte;
  /** Die Unternehmen, aus denen die Hausfarbe eines Kürzels kommt. */
  companies: readonly Unternehmen[];
  contact: ContactState;
  onTab: (tab: AktenTabId) => void;
};

const TITLE_ID = "cov-kopf-titel";

type Value = string | null | undefined;
const present = (value: Value): value is string => value !== null && value !== undefined && value.trim() !== "";
/** Der getrimmte Wert, den Anzeige, Verweis und Vergleich gemeinsam nutzen; leer und fehlend sind dasselbe (`null`). */
const trimmed = (value: Value): string | null => (present(value) ? value.trim() : null);
const sameEmail = (a: Value, b: Value): boolean => present(a) && present(b) && a.trim().toLowerCase() === b.trim().toLowerCase();
/** Dieselbe Nummer in anderer Schreibung (Leerzeichen, Klammern, Bindestriche) ist dieselbe Nummer. */
const samePhone = (a: Value, b: Value): boolean => telHref(a) !== null && telHref(a) === telHref(b);

/** Ein Weg zum Ansprechpartner: als Verweis, wenn er eine brauchbare Adresse hat, sonst als Text (der ebenso umbricht). */
function Channel(p: { text: string | null; href: (text: string) => string | null }): ReactElement | null {
  if (p.text === null) return null;
  const href = p.href(p.text);
  return href === null ? <span className="cov__kopf-text">{p.text}</span> : <a className="cov__kopf-link" href={href}>{p.text}</a>;
}

/** Telefon und E-Mail als Verweise (`tel:`, `mailto:`); was fehlt, bleibt weg. */
function Channels(p: { phone: Value; email: Value }): ReactElement {
  return (
    <>
      <Channel text={trimmed(p.phone)} href={telHref} />
      <Channel text={trimmed(p.email)} href={mailtoHref} />
    </>
  );
}

/** Eine Aussage mit dem Sprung dorthin, wo man sie ändert. */
function Missing(p: { text: string; link: string; onClick: () => void }): ReactElement {
  return (
    <div className="cov__kopf-leer">
      <p className="cov__kopf-hinweis">{p.text}</p>
      <KiKnopf art="leise" onClick={p.onClick}>{p.link}</KiKnopf>
    </div>
  );
}

function Contact(p: { contact: ContactState; onTab: HeaderCardProps["onTab"] }): ReactElement {
  const { contact } = p;
  if (contact.kind === "loading") return <p className="cov__kopf-hinweis">{COV_TEXT.contactLoading}</p>;
  if (contact.kind === "failed") return <p className="cov__kopf-hinweis">{COV_TEXT.contactFailed}</p>;
  if (contact.contact === null) {
    return <Missing text={COV_TEXT.noContact} link={COV_TEXT.contactsLink} onClick={() => p.onTab("ansprechpartner")} />;
  }
  const { displayName, jobTitle, phone, email } = contact.contact;
  const role = trimmed(jobTitle);
  return (
    <>
      <strong className="cov__kopf-name">{displayName}</strong>
      {role !== null && <span className="cov__kopf-funktion">{role}</span>}
      <Channels phone={phone} email={email} />
    </>
  );
}

export function HeaderCard(p: HeaderCardProps): ReactElement {
  const { customer, contact } = p;
  const mainContact = contact.kind === "ready" ? contact.contact : null;
  // Die Zentrale zeigt nur, was vom Hauptansprechpartner abweicht; gleiche Angaben stünden sonst zweimal da.
  const centralPhone = samePhone(customer.phone, mainContact?.phone) ? null : customer.phone;
  const centralEmail = sameEmail(customer.email, mainContact?.email) ? null : customer.email;
  return (
    <section className="cov__kopf" aria-labelledby={TITLE_ID}>
      <Karte titel={(
        <h2 id={TITLE_ID} className="cov__kopf-titel">
          <KiKnopf art="leise" onClick={() => p.onTab("stammdaten")}>
            <Objektsymbol art="kunde" /> {COV_TEXT.headerTitle}
          </KiKnopf>
        </h2>
      )}>
        <div className="cov__kopf-inhalt">
          {customer.relations.length > 0 && (
            <div className="cov__kopf-nummern" role="group" aria-label={COV_TEXT.numbersLabel}>
              {customer.relations.map((r) => (
                <div key={r.id} className="cov__kopf-beziehung">
                  <KundenChip r={r} colorHex={hausfarbe(p.companies, r.shortCode)} />
                  {/* Je Unternehmen „Kürzel · Kundennummer“ und die Rollen als Kennzeichen (Spec „Wortwahl“, Kopfkachel). */}
                  <PartnerRoleBadges relation={r} mitKuerzel={false} />
                </div>
              ))}
            </div>
          )}
          {customer.hauptanschriftZeilen.length > 0 ? (
            <address className="cov__kopf-anschrift" aria-label={COV_TEXT.addressLabel}>
              {customer.hauptanschriftZeilen.map((line, i) => <span key={`${i}:${line}`}>{line}</span>)}
            </address>
          ) : (
            <Missing text={COV_TEXT.noAddress} link={COV_TEXT.addressesLink} onClick={() => p.onTab("adressen")} />
          )}
          <div className="cov__kopf-kontakt" role="group" aria-label={COV_TEXT.contactLabel}>
            <Contact contact={contact} onTab={p.onTab} />
          </div>
          {(present(centralPhone) || present(centralEmail)) && (
            <div className="cov__kopf-zentrale">
              <span className="cov__kopf-hinweis">{COV_TEXT.central}</span>
              <Channels phone={centralPhone} email={centralEmail} />
            </div>
          )}
          <p className="cov__kopf-hinweis cov__kopf-aktivitaet">
            {`${trimmed(customer.industry) ?? COV_TEXT.industryUnknown} · ${COV_TEXT.lastActivity}: ${relativeZeit(customer.lastActivityAt)}`}
          </p>
        </div>
      </Karte>
    </section>
  );
}
